import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogBaseComponent } from '@/app/core/components';
import { ContactSelectorComponent } from '../entity-selection/contact-selector.component';
import { ContactFormComponent } from '../contact-form/contact-form.component';
import { ContactResponseDto } from '@qc/shared/dto';
import { ContactWithRole } from '../../interfaces/contact.interface';
import { Button } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';

export interface ContactToOrganizationData {
    contact: ContactResponseDto;
    roleDescription: string;
    context: string;
}

@Component({
    selector: 'qc-add-contact-to-organization-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ContactSelectorComponent,
        ContactFormComponent,
        Button,
        DropdownModule,
        InputTextModule,
    ],
    template: `
        <div class="w-[500px] max-w-[90vw] bg-white rounded-lg shadow-2xl overflow-visible">
            <div class="flex justify-between items-center p-6 border-b border-gray-200">
                <h2 class="text-xl font-semibold text-gray-900">
                    {{ isEditMode ? 'Edit Contact' : headerTitle }}
                </h2>
                <button type="button" class="p-2 hover:bg-gray-100 rounded-lg" (click)="close()">
                    <i class="pi pi-times text-gray-400"></i>
                </button>
            </div>

            <div class="p-6 max-h-[70vh] soverflow-x-visible">
                <div *ngIf="!showCreateForm" class="space-y-4">
                    <div>
                        <qc-contact-selector
                            label="Contact"
                            [required]="true"
                            [showCreateButton]="false"
                            [initialValue]="selectedContact"
                            (contactSelected)="onContactSelected($event)"
                        />

                        <div *ngIf="selectedContact" class="flex justify-end mt-2">
                            <button
                                type="button"
                                class="text-sm text-blue-600 hover:text-blue-800 underline"
                                (click)="editSelectedContact()"
                            >
                                Edit contact details
                            </button>
                        </div>
                    </div>

                    <div class="text-center" *ngIf="!selectedContact">
                        <button
                            type="button"
                            class="text-blue-600 hover:text-blue-800 underline"
                            (click)="showCreateNewContact()"
                        >
                            Or create a new contact
                        </button>
                    </div>

                    <div *ngIf="selectedContact" class="space-y-4">
                        <div>
                            <label class="block text-sm font-medium mb-1">Role Description</label>
                            <input
                                pInputText
                                [(ngModel)]="roleDescription"
                                placeholder="Enter role description"
                                class="w-full"
                            />
                        </div>

                        <div>
                            <label class="block text-sm font-medium mb-1">Context</label>
                            <input pInputText [(ngModel)]="context" placeholder="Enter context" class="w-full" />
                        </div>
                    </div>
                </div>

                <div *ngIf="showCreateForm" class="space-y-4">
                    <qc-contact-form
                        [config]="{ showRoleFields: false, showContextField: false }"
                        [contact]="contactForEdit"
                        [deferSave]="deferSave"
                        (save)="onContactCreated($event)"
                        (cancel)="cancelCreateContact()"
                    />
                </div>
            </div>

            <div *ngIf="!showCreateForm" class="flex justify-end gap-2 p-6 border-t border-gray-200">
                <p-button label="Cancel" severity="secondary" variant="outlined" (onClick)="close()" />
                <p-button
                    [label]="isEditMode ? 'Update Contact' : primaryButtonLabel"
                    severity="primary"
                    (onClick)="addContact()"
                    [disabled]="!selectedContact"
                />
            </div>
        </div>
    `,
})
export class AddContactToOrganizationDialogComponent extends DialogBaseComponent {
    @Input() deferSave = false;
    @Output() contactAdded = new EventEmitter<ContactToOrganizationData>();

    headerTitle = 'Add Contact to Organization';
    primaryButtonLabel = 'Add Contact';

    selectedContact: ContactResponseDto | null = null;
    roleDescription = '';
    context = '';
    showCreateForm = false;
    contactForEdit: ContactWithRole | null = null;
    isEditMode = false;

    constructor() {
        super();
    }

    setDialogLabels(labels: { headerTitle?: string; primaryButtonLabel?: string }): void {
        if (labels.headerTitle) {
            this.headerTitle = labels.headerTitle;
        }
        if (labels.primaryButtonLabel) {
            this.primaryButtonLabel = labels.primaryButtonLabel;
        }
    }

    setEditMode(contact: ContactWithRole): void {
        this.selectedContact = contact;
        this.roleDescription = contact.roleDescription || '';
        this.context = contact.context || '';
        this.isEditMode = true;

        this.primaryButtonLabel = 'Update Contact';
    }

    onContactSelected(contact: ContactResponseDto): void {
        this.selectedContact = contact;
        this.contactForEdit = null;
    }

    showCreateNewContact(): void {
        this.showCreateForm = true;
        this.contactForEdit = null;
    }

    editSelectedContact(): void {
        if (this.selectedContact) {
            this.contactForEdit = { ...this.selectedContact };
            this.showCreateForm = true;
        }
    }

    cancelCreateContact(): void {
        this.showCreateForm = false;
        this.contactForEdit = null;
    }

    onContactCreated(contact: ContactResponseDto): void {
        this.selectedContact = contact;
        this.showCreateForm = false;
        this.contactForEdit = null;
    }

    addContact(): void {
        if (this.selectedContact) {
            const contactData: ContactToOrganizationData = {
                contact: this.selectedContact,
                roleDescription: this.roleDescription,
                context: this.context,
            };

            this.contactAdded.emit(contactData);
            this.close(true);
        }
    }
}
